import React, { useState } from "react";
import { Filter, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";

/**
 * ApolloStyleFilters — Pannello filtri collassabile con facet multi-valore.
 *
 * Props:
 *   entity: "person" | "company"
 *   filters: object (state controllato dal padre)
 *   setFilters: fn(object)
 *   onApply: fn() — callback quando l'utente clicca "Applica filtri" (o Enter)
 */
export default function ApolloStyleFilters({ entity, filters, setFilters, onApply }) {
  const [open, setOpen] = useState(false);

  const set = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const reset = () => setFilters({});

  const activeCount = Object.values(filters).filter(v => v !== "" && v !== null && v !== undefined && v !== false).length;

  const personSelects = [
    {
      key: "department", label: "Reparto",
      opts: ["", "Executive", "Sales", "Marketing", "Engineering", "Finance", "Human Resources", "Procurement", "Operations", "R&D", "Legal", "Security", "Product", "Manufacturing", "Supply Chain", "Trade", "Data & Analytics", "ESG", "Innovation", "Business Development", "Quality", "Technology", "Revenue"],
    },
    {
      key: "seniority", label: "Seniority",
      opts: [
        ["", "Tutti"],
        ["c-level", "C-Level"],
        ["vp", "VP"],
        ["head", "Head of"],
        ["director", "Director"],
        ["manager", "Manager"],
      ],
    },
    {
      key: "location", label: "Location",
      opts: ["", "Italia", "Germania", "Spagna", "Francia", "Svizzera", "Polonia", "Olanda", "Milano", "Roma", "Berlin", "Madrid", "Zurigo", "Paris"],
    },
  ];

  const companySelects = [
    {
      key: "industry", label: "Settore",
      opts: ["", "Technology", "Manufacturing", "Finance", "Healthcare", "Food & Beverage", "Retail", "Logistics", "Energy", "Steel & Alloys", "Precision Mechanics", "Automotive", "Aerospace", "Chemicals", "Pharma", "Fintech"],
    },
    {
      key: "employees", label: "Dipendenti",
      opts: [
        ["", "Tutti"],
        ["1-10", "1–10"],
        ["11-50", "11–50"],
        ["51-200", "51–200"],
        ["201-1000", "201–1.000"],
        ["1001-5000", "1.001–5.000"],
        ["5000+", "5.000+"],
      ],
    },
    {
      key: "revenue", label: "Revenue",
      opts: [
        ["", "Tutti"],
        ["0-1M", "< 1M"],
        ["1M-10M", "1M–10M"],
        ["10M-50M", "10M–50M"],
        ["50M-250M", "50M–250M"],
        ["250M-1B", "250M–1B"],
        ["1B+", "1B+"],
      ],
    },
    {
      key: "location", label: "Location HQ",
      opts: ["", "Italia", "Germania", "Spagna", "Francia", "USA", "UK", "Milano", "Roma", "Berlin", "San Francisco"],
    },
  ];

  const selects = entity === "company" ? companySelects : personSelects;

  const personChecks = [
    { key: "verified",     label: "Solo verificati" },
    { key: "has_email",    label: "Con email" },
    { key: "has_linkedin", label: "Con LinkedIn" },
  ];
  const companyChecks = [
    { key: "verified",       label: "Solo verificate" },
    { key: "has_website",    label: "Con sito web" },
    { key: "has_tech_stack", label: "Con tech stack" },
  ];
  const checks = entity === "company" ? companyChecks : personChecks;

  const renderOpt = (opt) => {
    if (Array.isArray(opt)) return <option key={opt[0]} value={opt[0]}>{opt[1] || opt[0] || "Tutti"}</option>;
    return <option key={opt} value={opt}>{opt || "Tutti"}</option>;
  };

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04]" data-testid={`apollo-filters-${entity}`}>
      {/* Header toggle */}
      <button
        type="button"
        data-testid={`apollo-filters-toggle-${entity}`}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#12161C] transition-colors"
      >
        <Filter className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
        <span className="text-xs tracking-widest uppercase font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
          Filtri massivi {entity === "company" ? "aziende" : "persone"}
        </span>
        {activeCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-[10px] tabular-nums font-bold bg-[#00E5FF] text-black tracking-wider">
            {activeCount}
          </span>
        )}
        <span className="ml-auto text-[#A0AEC0]">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {/* Body */}
      {open && (
        <div className="px-4 py-4 border-t border-white/[0.04] space-y-4">
          {/* Facet selects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {selects.map(sel => (
              <div key={sel.key}>
                <label className="block text-[9px] tracking-widest uppercase text-[#A0AEC0] mb-1 font-semibold">{sel.label}</label>
                <select
                  data-testid={`apollo-filter-${entity}-${sel.key}`}
                  value={filters[sel.key] || ""}
                  onChange={e => set(sel.key, e.target.value)}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-2 text-xs focus:outline-none focus:border-[#00E5FF]"
                >
                  {sel.opts.map(renderOpt)}
                </select>
              </div>
            ))}
          </div>

          {/* Boolean checkboxes */}
          <div className="flex flex-wrap gap-2">
            {checks.map(chk => {
              const on = filters[chk.key] === true;
              return (
                <button
                  key={chk.key}
                  type="button"
                  data-testid={`apollo-filter-${entity}-${chk.key}`}
                  onClick={() => set(chk.key, !on)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider uppercase font-semibold border transition-all ${
                    on
                      ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                      : "bg-[#050505] text-[#CBD5E1] border-white/[0.04] hover:border-[#00E5FF]/60"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-black" : "bg-[#00E5FF]"}`} />
                  {chk.label}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1 border-t border-white/[0.04] mt-1">
            <button
              type="button"
              data-testid={`apollo-filters-apply-${entity}`}
              onClick={() => onApply?.()}
              className="bg-[#00E5FF] text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors"
            >
              Applica filtri
            </button>
            {activeCount > 0 && (
              <button
                type="button"
                data-testid={`apollo-filters-reset-${entity}`}
                onClick={reset}
                className="inline-flex items-center gap-1 border border-white/[0.04] bg-[#050505] text-[#CBD5E1] hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 text-[11px] uppercase tracking-widest transition-colors"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            )}
            <span className="ml-auto text-[9px] tracking-widest uppercase text-[#6B7280]">
              Motore Apollo-compatible · fino a 50 risultati
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
